import type { GraphQLError } from 'graphql';

import type { GQLError, MultiEditLineItemMutation } from '../../../../../../../apis/graphql';
import { campaignClient, createGraphQLErrorMap } from '../../../../../../../apis/graphql';
import { writeAdTargetingEmbellishmentFieldsToCache } from '../../../../../../../common/adTargeting';
import { AlertType } from '../../../../../../../common/Alert';
import { openToastAlert } from '../../../../../../../common/ToastAlert/toastAlert';
import { getEarliestDateRange, getLatestDateRange, getOldCacheData } from '../../../../../../../common/utils';
import { CacheEntityIdentifier } from '../../../../../../../configs/cache';
import type { TargetingTermValue as TargetingTermValueModel } from '../../../../../../../models';
import { LINE_ITEM_TRAFFICKING_COLUMNS } from '../../../../../TraffickingPage/hooks/fragments';
import type { MultiEditAdsMutation, ScheduleV5 } from './../../../../../../../apis/graphql/types';
import { AD_TRAFFICKING_COLUMN } from './../../../../../TraffickingPage/hooks/fragments/adFragments';
import type {
  AdFields,
  CacheUpdateFieldInput,
  GetNewSchedule,
  LineItemFields,
  MultipleEditOldCacheData,
} from './types';
import {
  AD_EDITS_FAILED,
  AD_EDITS_HAVE_BEEN_SAVED,
  LINE_ITEMS_EDITS_FAILED,
  LINE_ITEMS_EDITS_HAVE_BEEN_SAVED,
} from './utils/getRows/constants';

export const getConflictIdsWithoutDuplicates = (conflicts: Record<string, string[]> | undefined | null): string[] => {
  const result: string[] = [];

  if (conflicts) {
    Object.keys(conflicts).forEach((conflictKey) => {
      conflicts[conflictKey].forEach((id) => {
        if (!result.includes(id)) {
          result.push(id);
        }
      });
    });
  }

  return result;
};

export const getAdScheduleRanges = (schedule: ScheduleV5): GetNewSchedule => {
  const earliestStartDate = getEarliestDateRange(schedule)?.startDate;
  const latestEndDate = getLatestDateRange(schedule)?.endDate;

  return { earliestStartDate, latestEndDate };
};

export function getCacheUpdateFields({
  isLineItem,
  cacheData,
  schedule,
}: CacheUpdateFieldInput): LineItemFields | AdFields | Pick<AdFields, 'displayPublisherTarget'> {
  const {
    displayPublisherTarget,
    metrics,
    metadata,
    startDate,
    endDate,
    countries,
    orderLineItemDto,
    hasZeroDeliveryRisk,
    magniteDealDto,
    pacingMetrics,
    exclusiveBlock,
    subStatus,
    ssp,
    deal,
    dealMetadata,
    automatedDeliveryBufferPercent,
    staqError,
  } = cacheData;

  const fieldsData = { displayPublisherTarget };

  if (isLineItem) {
    const lineItemData = {
      metrics,
      metadata,
      startDate,
      endDate,
      countries,
      orderLineItemDto,
      hasZeroDeliveryRisk,
      magniteDealDto,
      pacingMetrics,
      exclusiveBlock,
      subStatus,
      ssp,
      deal,
      dealMetadata,
      automatedDeliveryBufferPercent,
      staqError,
    };
    Object.assign(fieldsData, lineItemData);
  }

  if (schedule) {
    const { earliestStartDate, latestEndDate } = getAdScheduleRanges(schedule);
    const scheduleData = { startDate: earliestStartDate, endDate: latestEndDate };

    Object.assign(fieldsData, scheduleData);
  }

  return fieldsData;
}

export const updateCacheItem = (
  isLineItem: boolean,
  data: MultiEditLineItemMutation | MultiEditAdsMutation,
  id: string
): MultiEditLineItemMutation['multiEditLineItem'][number] | MultiEditAdsMutation['multiEditAds'][number] | null => {
  if (isLineItem) {
    const lineItems = (data as MultiEditLineItemMutation).multiEditLineItem;
    const lineItem = lineItems.find((lineItem) => lineItem.id === id);

    return lineItem || null;
  } else {
    const ads = (data as MultiEditAdsMutation).multiEditAds;
    const ad = ads.find((ad) => ad.id === id);

    return ad || null;
  }
};

export const updateCacheData = <T>(
  isLineItem: boolean,
  data: MultiEditLineItemMutation | MultiEditAdsMutation,
  oldCacheData: MultipleEditOldCacheData<T>[]
): void => {
  oldCacheData.forEach(({ cacheId, cacheData, id, fragment }) => {
    const cacheUpdateData = updateCacheItem(isLineItem, data, id);

    if (cacheUpdateData !== null) {
      const fields = getCacheUpdateFields({
        isLineItem,
        cacheData,
        schedule: cacheUpdateData.schedule,
      });

      campaignClient.writeFragment({
        id: cacheId,
        fragment,
        data: {
          ...cacheUpdateData,
          ...fields,
        },
      });

      // For ad bulk edits that may have changed targeting, refresh CampaignSearcher-derived
      // fields (hasInvalidPublisher / hasInvalidCountry / displayPublisherTarget / countriesTarget)
      // locally from the just-saved targeting + parent line item constraints, so the table row
      // stops showing a stale invalid alert between the mutation response and the consumer
      // catch-up. Line item bulk edits don't touch these ad-level fields.
      if (!isLineItem) {
        const adResult = cacheUpdateData as NonNullable<ReturnType<typeof updateCacheItem>> & {
          targetingTermValues?: TargetingTermValueModel[] | null;
          lineItem?: {
            displayPublisherTarget?: string[] | null;
            countries?: string[] | null;
          } | null;
        };
        const submittedAdTermValues = adResult.targetingTermValues ?? [];
        const lineItemDisplayPublisherTarget = adResult.lineItem?.displayPublisherTarget ?? [];
        const lineItemCountries = adResult.lineItem?.countries ?? undefined;

        writeAdTargetingEmbellishmentFieldsToCache({
          cache: campaignClient.cache,
          adId: id,
          submittedAdTermValues,
          lineItemDisplayPublisherTarget,
          lineItemCountries,
          fallbackAdDisplayPublisherTarget: adResult.displayPublisherTarget ?? undefined,
        });
      }
    }
  });
};

export const getErrorDescription = (errors: GraphQLError[], filteredErrors?: (string | GQLError)[]): string => {
  let description: string = errors[0].message;

  if (filteredErrors && filteredErrors.length > 0) {
    if (typeof filteredErrors[0] === 'string') {
      description = filteredErrors[0];
    } else if (filteredErrors[0]?.error?.message) {
      description = filteredErrors[0].error.message;
    }
  }

  return description;
};

export const onHandleUpdateError = (isLineItem: boolean, errors?: GraphQLError[]): void => {
  if (errors && errors.length > 0) {
    const filteredErrors = Object.values(createGraphQLErrorMap(errors))[0];

    openToastAlert({
      alertType: AlertType.ERROR,
      message: isLineItem ? LINE_ITEMS_EDITS_FAILED : AD_EDITS_FAILED,
      description: getErrorDescription(errors, filteredErrors),
    });
  }
};

export const onHandleUpdateSuccess = (isLineItem: boolean, onSubmit: () => void): void => {
  openToastAlert({
    alertType: AlertType.SUCCESS,
    message: isLineItem ? LINE_ITEMS_EDITS_HAVE_BEEN_SAVED : AD_EDITS_HAVE_BEEN_SAVED,
  });

  onSubmit();
};

export const getMultipleEditOldCacheData = <T>(
  isLineItem: boolean,
  selectedEntitiesIds: string[]
): MultipleEditOldCacheData<T>[] =>
  selectedEntitiesIds.map((id) => {
    const fragment = isLineItem ? LINE_ITEM_TRAFFICKING_COLUMNS : AD_TRAFFICKING_COLUMN;
    const cacheIdPrefix = isLineItem ? CacheEntityIdentifier.LineItemV5 : CacheEntityIdentifier.AdV5;

    const oldCacheData = getOldCacheData({
      cacheIdPrefix,
      id,
      fragment,
    });

    return {
      id,
      fragment,
      ...oldCacheData,
    };
  });
